import { Link, useParams } from 'react-router-dom';
import Seo from '../components/Seo';
import GuideLayout from '../components/GuideLayout';
import { getGuide } from '../content/guides';

const SITE = 'https://landlordcomply.uk';

// Renders [text](url) and **bold** without dangerouslySetInnerHTML.
function Inline({ text }) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const [, label, url] = link;
      return url.startsWith('/')
        ? <Link key={i} to={url}>{label}</Link>
        : <a key={i} href={url} target="_blank" rel="noopener noreferrer">{label}</a>;
    }
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i}>{bold[1]}</strong>;
    return part;
  });
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function Block({ block }) {
  switch (block.t) {
    case 'h2':   return <h2>{block.text}</h2>;
    case 'p':    return <p><Inline text={block.text} /></p>;
    case 'note': return <div className="note"><p><Inline text={block.text} /></p></div>;
    case 'ul':   return <ul>{block.items.map((it, i) => <li key={i}><Inline text={it} /></li>)}</ul>;
    case 'ol':   return <ol>{block.items.map((it, i) => <li key={i}><Inline text={it} /></li>)}</ol>;
    default:     return null;
  }
}

export default function Guide() {
  const { slug } = useParams();
  const guide = getGuide(slug);

  if (!guide) {
    return (
      <GuideLayout>
        <Seo title="Guide not found | LandlordComply" path={`/guides/${slug}`} noindex />
        <div className="wrap">
          <h1>Guide not found</h1>
          <p>We couldn't find that guide. <Link to="/guides">See all landlord guides</Link>.</p>
        </div>
      </GuideLayout>
    );
  }

  const url = `${SITE}/guides/${guide.slug}`;
  const org = { '@type': 'Organization', name: 'LandlordComply', url: SITE };
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.title,
    description: guide.description,
    datePublished: guide.published,
    dateModified: guide.updated,
    mainEntityOfPage: url,
    author: org,
    publisher: org,
  };
  const related = (guide.related || []).map(getGuide).filter(Boolean);

  return (
    <GuideLayout>
      <Seo title={guide.seoTitle} description={guide.description} path={`/guides/${guide.slug}`} jsonLd={jsonLd} />
      <article className="wrap">
        <p className="crumbs"><Link to="/">Home</Link> / <Link to="/guides">Guides</Link></p>
        <h1>{guide.title}</h1>
        <p className="meta">Last reviewed {formatDate(guide.updated)} · {guide.readMins} min read · Sources: GOV.UK and HSE</p>

        {guide.blocks.map((b, i) => <Block key={i} block={b} />)}

        <div className="cta-box">
          <h2>{guide.cta.title}</h2>
          <p>{guide.cta.text}</p>
          <Link to={guide.cta.to} className="btn btn-mint">{guide.cta.label}</Link>
        </div>

        <div className="sources">
          <h2>Sources</h2>
          <ul>
            {guide.sources.map(s => (
              <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer">{s.label}</a></li>
            ))}
          </ul>
          <p className="disclaimer">This guide is general information for landlords in England, not legal advice. Rules and guidance change, so check the sources above before you rely on a deadline or figure.</p>
        </div>

        {related.length > 0 && (
          <div className="related">
            <h2>Related guides</h2>
            <ul>
              {related.map(r => <li key={r.slug}><Link to={`/guides/${r.slug}`}>{r.title}</Link></li>)}
            </ul>
          </div>
        )}
      </article>
    </GuideLayout>
  );
}
